/**
 * wayai eval session stop — cancel a running eval session.
 *
 * The recovery path when a `run-eval` process died without cancelling its own
 * session (gh #3708). A session that already finished is left as it is, so this
 * is safe to run when you don't know whether the session is still live.
 *
 * Usage:
 *   wayai eval session stop <session_id>
 *   wayai eval session stop <session_id> --hub <id>
 */

import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { classifyStopOutcome, describeSeedRelease, formatEvalResultsCommand } from '../lib/eval-session-control.js';
import { UUID_RE } from '../lib/utils.js';

export async function evalSessionStopCommand(args: string[]): Promise<void> {
  // resolveActiveHubId splices `--hub <value>` out of args, so the remaining
  // non-flag positional is unambiguously the session id.
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  const sessionId = args.find((arg) => !arg.startsWith('-'));
  if (!sessionId) {
    console.error('Usage: wayai eval session stop <session_id> [--hub <id>]');
    process.exit(1);
  }
  if (!UUID_RE.test(sessionId)) {
    console.error(`Invalid session id: ${sessionId}`);
    process.exit(1);
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  try {
    const outcome = classifyStopOutcome(await client.stopEvalSession(hubId, sessionId));
    if (outcome.kind === 'already_finished') {
      console.log(`Eval session ${sessionId} had already finished (${outcome.status}) — nothing was cancelled.`);
      console.log(`See its results: ${formatEvalResultsCommand(sessionId, hubId)}`);
    } else {
      console.log(`Cancellation requested for eval session ${sessionId}.`);
      console.log(describeSeedRelease(outcome.seedRelease));
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.error(`Eval session not found: ${sessionId}`);
      process.exit(1);
    }
    throw err;
  }
}
